<template>
  <div class="flex h-screen">
    <!-- Sidebar -->
    <aside class="w-11rem surface-100 text-white flex flex-column justify-content-between h-screen overflow-y-auto">
      <div class="mt-4 m-3">
        <div class="text-center">
          <img :src="logoImage" alt="Logo" class="w-5rem h-auto mb-2">
          <div>
            <p class="text-sm letter-spacing-2 text-gray-400 m-0">Welcome <span class="capitalize">{{ userName }}</span></p>
            <div class="mt-3">
              <div class="text-md mb-1 bg-gray-600 p-0 border-round-md">
                <span class="text-gray-400 mr-2">UK</span> 
                <span class="text-gray-200">{{ londonTime }}</span>
              </div>
              <div class="text-md bg-gray-600 p-0 border-round-md">
                <span class="text-gray-400 mr-2">PK</span> 
                <span class="text-gray-200">{{ pakistanTime }}</span>
              </div>
            </div>
          </div>
        </div>
        <ul class="list-none p-0 m-0 mt-2 border-top-1 border-gray-700 pt-2">
          <li class="mb-1">
            <router-link :to="dashboardDefaultRoute" class="flex text-sm align-items-center px-2 py-2 text-gray-300 hover:bg-gray-800 border-round-sm no-underline transition-colors transition-duration-150" :class="{ 'bg-gray-800 text-white': currentRoute.path.startsWith('/dashboard') }">
              <i class="pi pi-home mr-2 text-sm"></i> Dashboard
            </router-link>
          </li>
          <li class="mb-1">
            <router-link :to="attendanceDefaultRoute" class="flex text-sm align-items-center px-2 py-2 text-gray-300 hover:bg-gray-800 border-round-sm no-underline transition-colors transition-duration-150" :class="{ 'bg-gray-800 text-white': currentRoute.path.startsWith('/attendance') }">
              <i class="pi pi-key mr-2 text-sm"></i> Attendance
            </router-link>
          </li>
          <li class="mb-1">
            <router-link :to="rosterDefaultRoute" class="flex text-sm align-items-center px-2 py-2 text-gray-300 hover:bg-gray-800 border-round-sm no-underline transition-colors transition-duration-150" :class="{ 'bg-gray-800 text-white': currentRoute.path.startsWith('/roster') }">
              <i class="pi pi-calendar-plus mr-2 text-sm"></i> Roster
            </router-link>
          </li>
          <li class="mb-1">
            <router-link :to="leavesDefaultRoute" class="flex text-sm align-items-center px-2 py-2 text-gray-300 hover:bg-gray-800 border-round-sm no-underline transition-colors transition-duration-150" :class="{ 'bg-gray-800 text-white': currentRoute.path.startsWith('/leaves') }">
              <i class="pi pi-calendar-minus mr-2 text-sm"></i> Leaves
            </router-link>
          </li>
          <li class="mb-1 mt-2 pt-3 border-top-1 border-200">
            <router-link :to="coverCalendarDefaultRoute" class="flex text-sm align-items-center px-2 py-2 text-gray-300 hover:bg-gray-800 border-round-sm no-underline transition-colors transition-duration-150" :class="{ 'bg-gray-800 text-white': currentRoute.path.startsWith('/cover-calendar') }">
              <i class="pi pi-calendar-clock mr-2 text-sm"></i> Cover Calendar
            </router-link>
          </li>
          <li class="mb-1">
            <router-link :to="leaveCalendarDefaultRoute" class="flex text-sm align-items-center px-2 py-2 text-gray-300 hover:bg-gray-800 border-round-sm no-underline transition-colors transition-duration-150" :class="{ 'bg-gray-800 text-white': currentRoute.path.startsWith('/leave-calendar') || currentRoute.path.startsWith('/calendar') }">
              <i class="pi pi-calendar-times mr-2 text-sm"></i> Leave Calendar
            </router-link>
          </li>
          <li class="mb-1 mt-2 pt-3 border-top-1 border-200">
            <router-link to="/employees" class="flex text-sm align-items-center px-2 py-2 text-gray-300 hover:bg-gray-800 border-round-sm no-underline transition-colors transition-duration-150" exact-active-class="bg-gray-800 text-white">
              <i class="pi pi-user mr-2 text-sm"></i> Employees
            </router-link>
          </li>
          <li class="mb-1">
            <router-link to="/complaints" class="flex text-sm align-items-center px-2 py-2 text-gray-300 hover:bg-gray-800 border-round-sm no-underline transition-colors transition-duration-150" exact-active-class="bg-gray-800 text-white">
              <i class="pi pi-flag mr-2 text-sm"></i> Complaints
            </router-link>
          </li>
          <li class="mb-1">
            <router-link to="/insight" class="flex text-sm align-items-center px-2 py-2 text-gray-300 hover:bg-gray-800 border-round-sm no-underline transition-colors transition-duration-150" exact-active-class="bg-gray-800 text-white">
              <i class="pi pi-chart-line mr-2 text-sm"></i> Insight
            </router-link>
          </li>
          <li class="mb-1">
            <router-link to="/logs" class="flex text-sm align-items-center px-2 py-2 text-gray-300 hover:bg-gray-800 border-round-sm no-underline transition-colors transition-duration-150" exact-active-class="bg-gray-800 text-white">
              <i class="pi pi-code mr-2 text-sm"></i> Logs
            </router-link>
          </li>
        </ul>
      </div>
      <div class="p-3">
        <div class="text-center mb-2">
          <Button icon="pi pi-sync" class="p-button surface-200 hover:surface-300 px-3 py-2 mr-2" :disabled="isClearingData || loadingLogout" @click="performClearData"></Button>
          <Button icon="pi pi-power-off" class="p-button surface-200 hover:surface-300 px-3 py-2" :disabled="isClearingData || loadingLogout" @click="showLogoutDialog"></Button>
        </div>
        <div class="text-center line-height-1">
          <div class="opacity-80 mb-2">
            <div class="imab-logo mt-4">
              <div><span class="I">I</span><span class="M">M</span><span class="A">A</span><span class="B">B</span></div>
            </div>
            <span class="text-sm text-400" style="letter-spacing: .4rem;">core</span>
          </div>
          <div class="text-xs border-round-md lowercase bg-gray-600 text-400 p-1 w-10 mx-auto">{{ envName }} {{ envVersion }}</div>
        </div>
      </div>
    </aside>

    <!-- Main Content Area -->
    <main data-main-scroll-container class="flex-1 surface-0 overflow-y-auto h-screen">
      <div class="p-3">
        <PrimeToast />
        <PrimeToast position="bottom-right" group="br" />
        <router-view></router-view>
      </div>
    </main>
  </div>

  <!-- Logout Dialog -->
  <Dialog v-model:visible="showLogoutForm" :modal="true" :closable="false">
    <h3 class="m-0 p-0">Do you really want to Logout?</h3>
    <template #footer>
      <Button label="No" class="p-button-secondary mr-2" @click="showLogoutForm = false" :disabled="isClearingData || loadingLogout" />
      <Button label="Yes Logout" class="p-button-primary" @click="performLogoutConfirmation" :loading="loadingLogout" :disabled="loadingLogout" />
    </template>
  </Dialog>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useUser } from '@/composables/useUser';
import { useClearData } from '@/composables/useClearData';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import logo from '@/assets/orion-logo-white-bg.png';
import { useLondonClock } from '@/composables/time/useLondonTime';
import { usePakistanTime } from '@/composables/time/usePakistanTime';
import {
  buildDefaultRosterRoute,
  buildRosterRoute,
  parseRosterRouteFilters,
  parseRosterSortQuery
} from '@/composables/useRosterRouteFilters';
import {
  buildAttendanceRoute,
  buildDefaultAttendanceRoute,
  parseAttendanceRouteFilters,
  parseAttendanceSortQuery
} from '@/composables/useAttendanceRouteFilters';
import { buildDefaultCalendarRoute } from '@/composables/useCalendarRouteFilters';
import { buildDashboardRoute } from '@/composables/useDashboardRoute';

const router = useRouter();
const currentRoute = useRoute();
const authStore = useAuthStore();
const { logout } = authStore;

const { userName, userId } = useUser();
const { isClearingData, clearDataAndRefresh } = useClearData();

const logoImage = logo;
const showLogoutForm = ref(false);
const loadingLogout = ref(false);
const envName = process.env.VUE_APP_ENV_NAME;
const envVersion = process.env.VUE_APP_ENV_VERSION;

const { londonTime } = useLondonClock();
const { pakistanTime } = usePakistanTime();
const dashboardDefaultRoute = computed(() => {
  void londonTime.value;
  return buildDashboardRoute();
});

const workforceRouteContext = computed(() => {
  if (currentRoute.name === 'ViewAttendanceFiltered') {
    const filters = parseAttendanceRouteFilters(currentRoute.params);
    const sort = parseAttendanceSortQuery(currentRoute.query);
    if (!filters.valid || !sort.valid) return null;
    return {
      viewType: 'attendance',
      companyId: filters.companyId,
      employeeIds: filters.employeeIds,
      filterType: filters.filterType,
      shiftType: filters.shiftType,
      showNotLogout: filters.showNotLogout,
      startDate: filters.startDate,
      endDate: filters.endDate,
      page: filters.page,
      sort: sort.sort,
      order: sort.order
    };
  }

  if (
    currentRoute.name === 'ViewRosterFiltered'
    || currentRoute.name === 'ViewLeavesFiltered'
  ) {
    const viewType = currentRoute.name === 'ViewLeavesFiltered' ? 'leaves' : 'roster';
    const filters = parseRosterRouteFilters(currentRoute.params, viewType);
    const sort = parseRosterSortQuery(currentRoute.query);
    if (!filters.valid || !sort.valid) return null;
    return {
      viewType,
      companyId: filters.companyId,
      employeeIds: filters.employeeIds,
      markedFilter: filters.markedFilter,
      startDate: filters.startDate,
      endDate: filters.endDate,
      page: filters.page,
      sort: sort.sort,
      order: sort.order
    };
  }

  return null;
});

const buildWorkforceNavigationRoute = targetView => {
  void londonTime.value;
  const context = workforceRouteContext.value;
  if (!context) {
    return targetView === 'attendance'
      ? buildDefaultAttendanceRoute()
      : buildDefaultRosterRoute(targetView);
  }

  if (context.viewType === targetView && targetView !== 'attendance') {
    return currentRoute.fullPath;
  }

  if (context.viewType === 'attendance' && targetView === 'attendance') {
    return buildAttendanceRoute({
      companyId: context.companyId,
      employeeIds: context.employeeIds,
      filterType: context.filterType,
      shiftType: context.shiftType,
      showNotLogout: context.showNotLogout,
      startDate: context.startDate,
      endDate: context.endDate,
      page: context.page,
      sort: 'attendance',
      order: 'desc'
    });
  }

  const sharedFilters = {
    companyId: context.companyId,
    employeeIds: context.employeeIds,
    filterType: 'all',
    markedFilter: targetView === 'roster' ? (context.markedFilter || 'all') : 'all',
    startDate: context.startDate,
    endDate: context.endDate,
    page: context.page,
    sort: targetView === 'attendance' ? 'attendance' : 'roster',
    order: targetView === 'attendance' ? 'desc' : context.order
  };

  return targetView === 'attendance'
    ? buildAttendanceRoute(sharedFilters)
    : buildRosterRoute({ ...sharedFilters, viewType: targetView });
};

const rosterDefaultRoute = computed(() => {
  return buildWorkforceNavigationRoute('roster');
});
const leavesDefaultRoute = computed(() => {
  return buildWorkforceNavigationRoute('leaves');
});
const attendanceDefaultRoute = computed(() => {
  return buildWorkforceNavigationRoute('attendance');
});
const leaveCalendarDefaultRoute = computed(() => {
  void londonTime.value;
  return buildDefaultCalendarRoute('leaves');
});
const coverCalendarDefaultRoute = computed(() => {
  void londonTime.value;
  return buildDefaultCalendarRoute('covers');
});

const showLogoutDialog = () => {
  showLogoutForm.value = true;
};

const performLogoutConfirmation = async () => {
  loadingLogout.value = true;
  await logout({ userId: userId.value, currentTimeUnix: 0 });
  await clearDataAndRefresh();
  router.push('/login');
  loadingLogout.value = false;
  showLogoutForm.value = false;
};

const performClearData = async () => {
  isClearingData.value = true;
  await clearDataAndRefresh();
  router.push('/login');
  isClearingData.value = false;
};

</script>

<style>
body{
  padding: 0;
  margin: 0;
}
</style>
